import { SideImage } from '../../components/SideImage';
import { StickyFooter } from '../../components/StickyFooter';
import { BookingNotificationContent } from '../../composites/BookingNotificationContent';
import { SpaDaysBookingContent } from '../../composites/SpaDaysBookingContent';
import { SplitLayout } from '../../composites/SplitLayout';
import { HideOnMobile } from './SpaDaysBookingForm.styles';
import type { SpaDaysBookingFormProps } from './SpaDaysBookingForm.types';

export function SpaDaysBookingForm({
  imageUrl,
  imageAlt,
  onBack,
  telephoneOnly,
  unavailable,
  stickyFooter,
  labels,
  ...contentProps
}: SpaDaysBookingFormProps) {
  const {
    title,
    subtitle,
    calendar,
    timeslotChips,
    stickyFooter: stickyFooterLabels,
    telephoneOnly: telephoneOnlyLabels,
    unavailable: unavailableLabels,
    ...contentLabels
  } = labels;

  const showTelephoneOnly = telephoneOnly && !!telephoneOnlyLabels;
  const showUnavailable = unavailable && !!unavailableLabels;

  if (process.env.NODE_ENV !== 'production') {
    if (telephoneOnly && !telephoneOnlyLabels) {
      console.warn(
        'SpaDaysBookingForm: telephoneOnly is true but labels.telephoneOnly is missing. The booking form will render as normal.'
      );
    }
    if (unavailable && !unavailableLabels) {
      console.warn(
        'SpaDaysBookingForm: unavailable is true but labels.unavailable is missing. The booking form will render as normal.'
      );
    }
  }

  return (
    <SplitLayout
      left={
        <HideOnMobile>
          <SideImage src={imageUrl} alt={imageAlt} sticky onBack={onBack} />
        </HideOnMobile>
      }
      right={
        showTelephoneOnly ? (
          <BookingNotificationContent
            title={title}
            subtitle={subtitle}
            variant="telephoneOnly"
            notificationLabels={telephoneOnlyLabels!}
          />
        ) : showUnavailable ? (
          <BookingNotificationContent
            title={title}
            subtitle={subtitle}
            variant="unavailable"
            notificationLabels={{
              ...unavailableLabels!,
              onCtaClick: unavailableLabels!.onCtaClick ?? onBack,
            }}
            reservationProps={{
              title: contentLabels.reservation?.title,
              subtitle: contentLabels.reservation?.subtitle,
              guestLabel: contentLabels.reservation?.guestLabel,
              guestCount: contentProps.guestCount,
              minGuests: contentProps.minGuests,
              maxGuests: contentProps.maxGuests,
              guestStep: contentProps.guestStep,
              onGuestCountChange: contentProps.onGuestCountChange,
              activeDate: contentProps.activeDate,
              selectedDate: contentProps.selectedDate,
              availability: contentProps.availability,
              onDateSelect: contentProps.onDateSelect,
              onWeekNavigate: contentProps.onWeekNavigate,
              onCalendarClick: contentProps.onCalendarClick,
              calendarLabels: calendar,
              counterDecreaseLabel: contentLabels.counter?.decrease,
              counterIncreaseLabel: contentLabels.counter?.increase,
            }}
          />
        ) : (
          <>
            <SpaDaysBookingContent
              {...contentProps}
              title={title}
              subtitle={subtitle}
              calendarLabels={calendar}
              labels={{ ...contentLabels, timeslotChips }}
            />
            {stickyFooter && stickyFooterLabels && (
              <StickyFooter
                {...stickyFooter}
                totalLabel={stickyFooterLabels.totalLabel}
                ctaLabel={stickyFooterLabels.ctaLabel}
              />
            )}
          </>
        )
      }
      contentGap="0px"
      contentMaxWidth="100%"
    />
  );
}
